/**
 * Nexi SoftPOS JavaScript Interface
 * 
 * Bridge between React app and native Android Nexi SoftPOS plugin
 * 
 * NOTE: Current implementation uses MOCK PROVIDER for testing.
 * Real SDK integration requires:
 * - Nexi SDK installation
 * - Backend token provisioning
 * - Physical Android device with NFC
 */
import { Plugins } from '@capacitor/core';

const { NexiSoftpos } = Plugins;

// Environment flag to use mock or real provider
const USE_MOCK = process.env.REACT_APP_USE_NEXI_MOCK !== 'false';

/**
 * Mock Provider for testing without hardware
 */
class NexiMockProvider {
    async initialize() {
        console.log('[MOCK] Initializing Nexi SoftPOS');
        return {
            success: true,
            installed: true,
            provider: 'mock'
        };
    }
    
    async startPayment(paymentData) {
        console.log('[MOCK] Starting payment:', paymentData);
        
        // Simulate payment processing delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Simulate different outcomes based on amount (for testing)
        const amount = parseFloat(paymentData.amount);
        const cents = Math.round((amount % 1) * 100);
        
        // Amount ending in .99 = cancelled
        if (cents === 99) {
            return {
                success: false,
                status: 'cancelled',
                message: 'Payment cancelled by user'
            };
        }
        
        // Amount ending in .88 = failed
        if (cents === 88) {
            return {
                success: false,
                status: 'failed',
                errorCode: 'CARD_DECLINED',
                message: 'Card was declined'
            };
        }
        
        // Default = success
        return {
            success: true,
            status: 'success',
            transactionId: `MOCK_TXN_${Date.now()}`,
            message: 'Payment successful'
        };
    }
    
    async cancelPayment() {
        console.log('[MOCK] Cancelling payment');
        return { success: true };
    }
}

class NexiSoftposService {
    
    constructor() {
        this.provider = USE_MOCK ? new NexiMockProvider() : NexiSoftpos;
        console.log(`[Nexi] Using ${USE_MOCK ? 'MOCK' : 'REAL'} provider`);
    }
    
    /**
     * Initialize Nexi SoftPOS plugin
     * Checks if Nexi POS app is installed
     */
    async initialize() {
        try {
            const result = await this.provider.initialize();
            return result;
        } catch (error) {
            console.error('Failed to initialize Nexi SoftPOS:', error);
            return {
                success: false,
                installed: false,
                error: error.message
            };
        }
    }
    
    /**
     * Start payment flow
     * 
     * @param {Object} paymentData
     * @param {string} paymentData.amount - Amount in format "10.00"
     * @param {string} paymentData.currency - Currency code (default: EUR)
     * @param {string} paymentData.reference - Unique transaction reference
     * @param {string} paymentData.merchantId - Merchant ID (optional)
     * @param {string} paymentData.terminalId - Terminal ID (optional)
     * @returns {Promise<Object>} Payment result
     */
    async startPayment(paymentData) {
        try {
            // Validate required fields
            if (!paymentData.amount) {
                throw new Error('Amount is required');
            }
            if (!paymentData.reference) {
                throw new Error('Reference is required');
            }
            
            // Format amount to 2 decimals
            const formattedAmount = parseFloat(paymentData.amount).toFixed(2);
            
            const params = {
                amount: formattedAmount,
                currency: paymentData.currency || 'EUR',
                reference: paymentData.reference,
                merchantId: paymentData.merchantId || '',
                terminalId: paymentData.terminalId || ''
            };
            
            console.log('Starting Nexi payment:', params);
            
            const result = await this.provider.startPayment(params);
            
            console.log('Nexi payment result:', result);
            
            return result;
            
        } catch (error) {
            console.error('Payment failed:', error);
            return {
                success: false,
                status: 'error',
                message: error.message
            };
        }
    }
    
    /**
     * Cancel ongoing payment
     */
    async cancelPayment() {
        try {
            await this.provider.cancelPayment();
            return { success: true };
        } catch (error) {
            console.error('Failed to cancel payment:', error);
            return { success: false, error: error.message };
        }
    }
    
    /**
     * Check if running in Capacitor (native app)
     */
    isNativeApp() {
        return typeof NexiSoftpos !== 'undefined';
    }
}

// Singleton instance
const nexiSoftposService = new NexiSoftposService();

export default nexiSoftposService;
